import { useLanguagePreference } from "@hooks:useLanguagePreference";
import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  LANGUAGE_FLAGS,
  LANGUAGE_LABELS,
  SupportedLanguage,
  VISIBLE_LANGUAGES,
} from "@libs:i18n";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { SxProps } from "@mui/material/styles";
import { forwardRef, Ref, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa6";
import { MdLanguage } from "react-icons/md";

interface LanguageSwitcherProps {
  sx?: SxProps;
  // "button" (default): a standalone trigger.
  // "menuItem": a full-width row that fits inside a Menu.
  variant?: "button" | "menuItem";
}

/**
 * Switches the UI language and persists the choice (i18next caches it to localStorage).
 * Works with or without a signed-in user.
 */
export const LanguageSwitcher = forwardRef<HTMLElement, LanguageSwitcherProps>(
  ({ sx, variant = "button" }, ref) => {
    const { t, i18n } = useTranslation();
    const { setLanguage } = useLanguagePreference();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const detected = i18n.resolvedLanguage ?? i18n.language;
    const current: SupportedLanguage = isSupportedLanguage(detected)
      ? detected
      : DEFAULT_LANGUAGE;

    const handleSelect = (language: SupportedLanguage) => {
      setLanguage(language);
      setAnchorEl(null);
    };

    // Flag (if configured) + name.
    const display = (language: string) => {
      const flag = LANGUAGE_FLAGS[language];
      const label = LANGUAGE_LABELS[language];
      return flag ? `${flag} ${label}` : label;
    };

    const isMenuItem = variant === "menuItem";

    return (
      <>
        {isMenuItem ? (
          <MenuItem
            ref={ref as Ref<HTMLLIElement>}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            aria-label={t("language.label")}
            sx={{ display: "flex", alignItems: "center", gap: "0.5rem", ...sx }}
          >
            <MdLanguage />
            {display(current)}
          </MenuItem>
        ) : (
          <Button
            ref={ref as Ref<HTMLButtonElement>}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            startIcon={<MdLanguage />}
            aria-label={t("language.label")}
            sx={{
              textTransform: "none",
              color: "gray.600",
              fontWeight: 600,
              ...sx,
            }}
          >
            {display(current)}
          </Button>
        )}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            horizontal: isMenuItem ? "left" : "right",
            vertical: isMenuItem ? "top" : "bottom",
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
          {VISIBLE_LANGUAGES.map((language) => (
            <MenuItem
              key={language}
              selected={language === current}
              onClick={() => handleSelect(language)}
            >
              <ListItemIcon sx={{ minWidth: "1.75rem" }}>
                {language === current ? <FaCheck /> : null}
              </ListItemIcon>
              <ListItemText>{display(language)}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  },
);

LanguageSwitcher.displayName = "LanguageSwitcher";
